import React, { FC, useRef, memo, useMemo } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import styled from 'styled-components';
import { Row } from '@tanstack/react-table';
import { EmptyResults } from '../EmptyResults/EmptyResults';
import { useCompare, useDeepCompare, useDeepMemo } from '../../hooks/useCompare';
import * as DataTableTypes from './utils/DataTableTypes';
import { useDataTableContext } from './utils/DataTableContext';
import { Thead } from './DataTableBasicElements/Thead';
import { DataTableRow } from './DataTableRow';
import { DataTableSkeleton } from './DataTableSkeleton';
import { DataTableErrorInternalComponent } from './DataTableAdvancedElements/DataTableError';
import { getFirstVisibleColumn } from './utils/getFirstVisibleColumn';
import { PluginDestination, usePluginContext } from './plugins/impl';
import { usePropsForwarderContext } from './plugins/impl/context/ForwarderContext';
import { getCellWidth, sanitizeId } from './utils/getCellWidth';
import { UtilityColumns, menuColumnsIds } from './hooks/columnHooks';
import {
  useDataTableFilterChangeObserver,
  useDataTablePageChangeObserver,
  useDataTableSortChangeObserver
} from './utils/DataTableQueryProvider/DataTableQueryProvider';

const isTestingEnv = process.env.NODE_ENV === 'test';

interface Props {
  dataTableId: string;
  emptyResultsMessage: string | undefined;
}

export const DataTableBody: FC<React.PropsWithChildren<Props>> = ({ emptyResultsMessage, dataTableId }) => {
  const tableRef = useRef<HTMLDivElement>(null);
  const isEmptyResultsVisible = usePluginContext().isDestinationDefined('body');
  const { getProps } = usePropsForwarderContext();
  const { error } = getProps('error') ?? {};
  const {
    isFetching,
    tableHeight,
    border,
    rowHeight,
    getRowModel,
    getAllColumns,
    getState,
    getHeaderGroups,
    resizeTableColumns
  } = useDataTableContext();
  const state = getState?.();
  const isResizing = !!state?.columnSizingInfo.isResizingColumn;
  const columns = getAllColumns?.();
  const headers = getHeaderGroups?.()?.flatMap((headerGroup) => headerGroup.headers) ?? [];

  const firstVisibleColumn = useMemo(
    () => getFirstVisibleColumn(state?.columnOrder, state?.hiddenColumns, columns),
    [columns, state?.columnOrder, state?.hiddenColumns]
  );

  const rows = getRowModel?.().rows ?? [];

  // compares for better performance
  const dataHasChanged =
    useDeepCompare({
      ...rows?.map((row) => {
        return {
          ...row.original,
          isSelected: row.getIsSelected(),
          ...row.getVisibleCells()?.map((col) => col.column)
        };
      }),
      isResizing
    }) && !isResizing;

  // Force remapping of values only when columnSizing changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: exception
  const columnSizeVars = useDeepMemo(() => {
    return headers
      .map((header) => {
        const sanitizedId = sanitizeId(header.id);
        const isResized = !!state?.columnSizing[header.id];
        const defaultWidth = header.column.columnDef.meta?.defaultWidth;

        const headerVar = `--header-${sanitizedId}-size`;
        const colVar = `--col-${sanitizeId(header.column.id)}-size`;

        // set fixed width for menu column
        if (menuColumnsIds.includes(header.id as UtilityColumns)) {
          return {
            [headerVar]: 40,
            [colVar]: 40
          };
        }

        return {
          [headerVar]: !isResized && defaultWidth ? defaultWidth : getCellWidth(header.getSize),
          [colVar]: !isResized && defaultWidth ? defaultWidth : getCellWidth(header.column.getSize)
        };
      })
      .reduce((acc, curr) => ({ ...acc, ...curr }), {});
  }, [state?.columnSizing, headers?.length]);

  const firstRowHasChanged = useCompare(firstVisibleColumn);

  useDataTableFilterChangeObserver(dataTableId, (event) => {
    if (event.detail && tableRef?.current?.scrollTo) {
      // reset horizontal and vertical scroll position
      tableRef.current.scrollTo(0, 0);
    }
  });

  useDataTableSortChangeObserver(dataTableId, (event) => {
    if (event.detail && tableRef?.current?.scrollTo) {
      // scroll only to top, keep horizontal scroll position
      tableRef.current.scrollTo({ top: 0 });
    }
  });

  useDataTablePageChangeObserver(dataTableId, (event) => {
    if (event.detail && tableRef?.current?.scrollTo) {
      // scroll only to top, keep horizontal scroll position
      tableRef.current.scrollTo({ top: 0 });
    }
  });

  return (
    <StyledTbody data-test="data-table-body" style={columnSizeVars}>
      <AutoSizer>
        {({ width, height }) => (
          <div
            className="data-table-body__auto-sizer"
            style={{
              width: isTestingEnv ? '500px' : width,
              height: isTestingEnv ? '500px' : height,
              overflowX: 'auto',
              overflowY: tableHeight === 'content' ? 'hidden' : 'auto'
            }}
            ref={tableRef}
          >
            {!error ? (
              <>
                <Thead calculatedTableHeight={height} />
                <StyledContent>
                  {!isFetching && !rows?.length ? (
                    isEmptyResultsVisible ? (
                      <PluginDestination name="body" />
                    ) : (
                      <StyledEmptyResults message={emptyResultsMessage} />
                    )
                  ) : null}
                  <DataTableSkeleton
                    resizeTableColumns={resizeTableColumns}
                    border={border}
                    rowHeight={rowHeight}
                    headers={headers}
                    isFetching={isFetching}
                    tableHeight={tableHeight}
                  />
                  <MemoizeRows
                    resizeTableColumns={resizeTableColumns}
                    firstVisibleColumn={firstVisibleColumn}
                    rowHeight={rowHeight}
                    border={border}
                    rows={rows}
                    dataHasChanged={dataHasChanged || firstRowHasChanged}
                    isFetching={isFetching}
                  />
                </StyledContent>
              </>
            ) : (
              <DataTableErrorInternalComponent />
            )}
          </div>
        )}
      </AutoSizer>
    </StyledTbody>
  );
};

interface MemoizeRowProps {
  rows: Row<object>[];
  dataHasChanged: boolean;
  border: DataTableTypes.DataTableBorder | undefined;
  rowHeight: DataTableTypes.DataTableRowHeight | undefined;
  firstVisibleColumn: string | undefined;
  resizeTableColumns: boolean;
  isFetching: boolean | undefined;
}

const MemoizeRows: FC<React.PropsWithChildren<MemoizeRowProps>> = memo(
  ({ rows = [], border, rowHeight, firstVisibleColumn, resizeTableColumns, isFetching }) => {
    return (
      <StyledRowsContainer $isFetching={isFetching}>
        {rows.map((row) => (
          <DataTableRow
            resizeTableColumns={resizeTableColumns}
            rowHeight={rowHeight}
            firstVisibleColumn={firstVisibleColumn}
            border={border}
            isSelected={row.getIsSelected()}
            key={row.id}
            row={row}
          />
        ))}
      </StyledRowsContainer>
    );
  },
  (_prev, next) => !next.dataHasChanged
);

const StyledTbody = styled.div`
  height: 100%;
  .data-table-body__auto-sizer {
    &::-webkit-scrollbar-thumb:vertical {
      border: 4px solid rgba(0, 0, 0, 0);
      background-clip: padding-box;
    }

    &::-webkit-scrollbar {
      width: 14px;
      height: 8px;
    }

    &::-webkit-scrollbar-track {
      background: rgb(0, 0, 0, 0);
    }

    &::-webkit-scrollbar-thumb {
      background: rgb(136, 136, 136, 0.4);
      border-radius: 8px;
    }
  }
`;

const StyledContent = styled.div`
  position: relative;
  height: 100%;
`;

const StyledEmptyResults = styled(EmptyResults)`
  position: absolute;
  top: 50%;
  left: 50%;
  margin-top: -75px;
  margin-left: -150px;
`;

const StyledRowsContainer = styled.div<{ $isFetching?: boolean }>`
  // Hide rows when fetching to prevent them from showing through 
  // the skeleton loader, especially a problem when rows are selected.
  visibility: ${({ $isFetching }) => ($isFetching ? 'hidden' : 'visible')};
`;
